import styled, { css } from 'styled-components';
import { AngleBoldRight16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import type { DefaultComponentProps } from '../../types';
import { useSocialFeedSettings } from '../../utils';

export const ARROW_WIDTH = 32;

interface TabsSliderArrowProps {
  direction: 'left' | 'right';
  onClick: () => void;
  visible: boolean;
}

export function TabsSliderArrow({
  direction,
  onClick,
  visible
}: TabsSliderArrowProps) {
  const { tabsTextColor } = useSocialFeedSettings();

  return (
    <Arrow
      onClick={onClick}
      _direction={direction}
      _show={visible}
      _textColor={tabsTextColor}
    >
      <ArrowIcon />
    </Arrow>
  );
}

function ArrowIcon({ className }: DefaultComponentProps) {
  const { tabsTextColor } = useSocialFeedSettings();

  return (
    <StyledIcon
      className={className}
      size={16}
      component={AngleBoldRight16}
      _textColor={tabsTextColor}
    />
  );
}

const StyledIcon = styled(Icon).attrs<{
  _textColor: string;
}>(({ _textColor }) => ({
  className: createReadableClass({
    component: 'tabs-slider',
    element: 'arrow-icon'
  }),
  fill: _textColor
}))`
  z-index: 2;
  width: ${ARROW_WIDTH}px;
  height: ${ARROW_WIDTH}px;
  border-radius: 50%;
  transition: background-color ease 0.3s;

  &:hover {
    background-color: ${({ _textColor }) =>
      getColorWithAlpha(_textColor, 0.05)};
  }
`;

const Arrow = styled.div.attrs<{
  _direction: 'left' | 'right';
  _show: boolean;
  _textColor: string;
}>(({ _direction }) => ({
  className: createReadableClass({
    component: 'tabs-slider',
    element: 'arrow',
    index: _direction
  })
}))`
  top: 50%;
  transform: translateY(-50%);
  position: absolute;
  z-index: 2;
  width: ${ARROW_WIDTH}px;
  height: ${ARROW_WIDTH}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;
  opacity: ${({ _show }) => (_show ? 1 : 0)};
  pointer-events: ${({ _show }) => (_show ? 'all' : 'none')};

  &:not(:hover)::after {
    content: '';
    position: absolute;
    height: 22px;
    left: -1px;
    width: 1px;
    background-color: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.1)};
  }

  ${({ _direction }) => {
    if (_direction === 'right') {
      return css`
        right: 0;
      `;
    }

    return css`
      left: 0;
      transform: translateY(-50%) rotateZ(-180deg);
    `;
  }};
`;
